import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Plus, FolderOpen, Users, LogOut, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const { user, logout, getAuthHeaders, API } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, getAuthHeaders());
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, newProject, getAuthHeaders());
      setNewProject({ name: '', description: '' });
      setCreateOpen(false);
      fetchProjects();
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glassmorphism sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight text-slate-900">Creaegis Nexus</h1>
            <p className="text-sm text-slate-600 font-mono uppercase tracking-wider">Investment Intelligence</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900"
              data-testid="logout-button"
            >
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-4xl font-heading font-bold tracking-tight text-slate-900 mb-2">Projects</h2>
            <p className="text-lg text-slate-600">Collaborative research workspaces</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary flex items-center gap-2" data-testid="create-project-button">
                <Plus size={18} />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading">Create Project</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Project Name</label>
                  <Input
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    placeholder="Q1 2026 Investment Analysis"
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description (Optional)</label>
                  <Textarea
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    placeholder="Evaluating SaaS companies in the $10-50M ARR range..."
                    rows={3}
                    data-testid="project-description-input"
                  />
                </div>
                <Button type="submit" className="btn-primary w-full" data-testid="create-project-submit">
                  Create Project
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="mx-auto text-slate-300 mb-4" size={64} />
            <h3 className="text-xl font-medium text-slate-900 mb-2">No projects yet</h3>
            <p className="text-slate-600 mb-6">Create your first project to start collaborating</p>
            <Button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="empty-create-project-button">
              <Plus size={18} className="mr-2" />
              Create Project
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div
                key={project.id}
                onClick={() => navigate(`/projects/${project.id}`)}
                className="card-interactive p-6 group"
                data-testid="project-card"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <FolderOpen className="text-blue-600" size={24} />
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500 font-mono">
                    <Calendar size={14} />
                    {format(new Date(project.created_at), 'MMM dd')}
                  </div>
                </div>
                <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {project.name}
                </h3>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                  {project.description || 'No description'}
                </p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Users size={14} />
                  <span>{project.members.length + 1} member{project.members.length !== 0 ? 's' : ''}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}